import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, ChevronDown, ChevronUp } from "lucide-react";

const Events = () => {
  const [expandedEvent, setExpandedEvent] = useState(null);

  const events = [
    {
      id: "0",
      title: "Chamba Beej Khojo Abhiyan Reaches Kugti Tribal Village",
      date: "2026-06-24",
      category: "Seed Conservation",
      images: ["/P-1.jpeg", "/P-2.jpeg"],
      videos: ["/V-1.mp4", "/V-2.mp4", "/V-3.mp4", "/V-4.mp4"],
      description: `PAHAD Trust on 24 June 2026 extended the Chamba Beej Khojo Abhiyan to Kugti, a remote high-altitude Gaddi tribal village in Bharmour, District Chamba, with support from M3M Foundation and DoCC.

Guided by Ms. Reena Kumari, Community Resource Person and Champion Farmer, the team interacted door-to-door with around 35–40 farmers to understand local crops, traditional seed diversity and livelihood opportunities.

While Rajmah, barley and potato remain the main crops, traditional varieties of proso millet, foxtail millet, finger millet and buckwheat have nearly disappeared from local farms.

In collaboration with RGPK3Y, Department of Agriculture, Himachal Pradesh, PAHAD Trust will work with farmers, seed saviours, Panchayat institutions and local departments to revive traditional seeds through seed documentation, demonstration plots, farmer training, seed multiplication and a community-managed seed bank.

Our aim is to strengthen local seed self-reliance, conserve mountain biodiversity and promote niche products such as Kugti Rajmah for sustainable tribal livelihoods.`,
    },
    {
      id: "1",
      title: "Himachal Pradesh Flood Relief 2025",
      date: "2025-01-20",
      category: "Disaster Relief",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/xrq1y5c2_WhatsApp%20Image%202026-03-29%20at%2008.53.51%20%284%29.jpeg",
      description: "PAHAD Trust organized emergency response for Himachal Pradesh Flood Relief 2025. Supported by Ashraya Hastha from Bangalore, the initiative provided essential relief materials to over 500 affected farming households across Chamba and Mandi districts. The relief packages included food supplies, tarpaulins, and basic necessities distributed through yellow relief packets to communities in remote hill areas.",
    },
    {
      id: "2",
      title: "PAHAD Organics Exhibition at Food Festival",
      date: "2025-02-15",
      category: "Exhibition",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/qoy1k25c_WhatsApp%20Image%202026-03-29%20at%2008.54.00.jpeg",
      description: "PAHAD Organics showcased traditional and organic products at the district-level food festival. The exhibition featured natural farming produce including millets, traditional seeds, organic honey, spices, and value-added products. The stall received recognition from agriculture department officials and attracted significant interest from visitors promoting awareness about chemical-free farming and nutritious traditional foods.",
    },
    {
      id: "3",
      title: "Chamba Beej Utsav 2025 - Traditional Seed Festival",
      date: "2025-03-15",
      category: "Event",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/uouilzrc_WhatsApp%20Image%202026-03-29%20at%2021.00.34.jpeg",
      description: "Annual seed festival bringing together farmers from 25 villages to preserve traditional seed varieties and celebrate mountain heritage. The Chamba Beej Khojo Abhiyan event featured seed exchange programs, community seed banks establishment, and recognition of local seed saviours. The event was held at Bhuri Singh Museum Chamba with participation from government officials, agriculture department representatives, and local farmers. Farmers shared indigenous wheat, barley, mustard, and millet varieties, strengthening biodiversity conservation efforts in the region.",
    },
    {
      id: "4",
      title: "Women Farmers Training on Traditional Seeds",
      date: "2025-02-28",
      category: "Training",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/imkc6uls_WhatsApp%20Image%202026-03-29%20at%2021.00.36.jpeg",
      description: "Three-day intensive training program for women farmers on traditional seed preservation and organic farming practices. Conducted on terraced hillside fields, the program focused on seed selection, storage techniques, and natural farming methods. Women farmers from Chakla, Paliur, and surrounding villages learned about climate-resilient indigenous varieties and shared their experiences in cultivating traditional grains including Kodra, Jou (barley), and local wheat varieties.",
    },
    {
      id: "5",
      title: "Women SHG Meeting - Sustainable Agriculture",
      date: "2025-03-01",
      category: "Workshop",
      image: "https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/tn0wpjb9_WhatsApp%20Image%202026-03-29%20at%2021.00.37.jpeg",
      description: "Monthly Self Help Group (SHG) meeting with women farmers to discuss sustainable agriculture practices and livelihood opportunities. The gathering brought together farmers from multiple villages to share experiences about organic farming, millet cultivation, and collective marketing strategies. PAHAD Trust team facilitated discussions on Jeevamrit preparation, natural pest management, and building community resilience against climate change challenges.",
    },
  ];

  const toggleEvent = (eventId) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1701153758624-4b3d352bcfbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwaGltYWxheWFzJTIwbW91bnRhaW4lMjBsYW5kc2NhcGUlMjBkaGFyYW1zaGFsYXxlbnwwfHx8fDE3NzQ3NjM2OTd8MA&ixlib=rb-4.1.0&q=85')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/50" />
        </div>
        <div className="relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white"
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
          >
            Events & News
          </motion.h1>
        </div>
      </section>

      {/* Events List */}
      <section className="py-24 bg-[#F9F8F6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`event-card-${index}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-[#E4E2DC]/40"
              >
                <button
                  onClick={() => toggleEvent(event.id)}
                  className="w-full p-6 text-left hover:bg-[#F9F8F6] transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="flex items-center space-x-2 text-sm text-[#8C9A74]">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(event.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                        {event.category && (
                          <span className="inline-block px-3 py-1 bg-[#2B5C45]/10 text-[#2B5C45] text-xs font-medium rounded-full">
                            {event.category}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-heading font-medium text-[#1C221F] mb-2">
                        {event.title}
                      </h3>
                      {expandedEvent !== event.id && (
                        <p className="text-sm text-[#515E58] line-clamp-2">
                          {event.description}
                        </p>
                      )}
                    </div>
                    <div className="ml-4">
                      {expandedEvent === event.id ? (
                        <ChevronUp className="h-6 w-6 text-[#2B5C45]" />
                      ) : (
                        <ChevronDown className="h-6 w-6 text-[#2B5C45]" />
                      )}
                    </div>
                  </div>
                </button>

                <AnimatePresence>
                  {expandedEvent === event.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        {event.images && (
                          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {event.images.map((src, i) => (
                              <div key={i} className="rounded-lg overflow-hidden">
                                <img
                                  src={src}
                                  alt={event.title}
                                  className="w-full h-auto object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {event.videos && (
                          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {event.videos.map((src, i) => (
                              <div key={i} className="rounded-lg overflow-hidden">
                                <video
                                  src={src}
                                  controls
                                  className="w-full h-auto object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {!event.images && event.image && (
                          <div className="mb-4 rounded-lg overflow-hidden">
                            <img
                              src={event.image}
                              alt={event.title}
                              className="w-full h-auto object-cover"
                            />
                          </div>
                        )}
                        <div className="prose prose-sm max-w-none">
                          <p className="text-base text-[#515E58] leading-relaxed whitespace-pre-line">
                            {event.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
